import { Col, Form, Row, Select } from 'antd';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WebConfigEn } from 'src/constants/webConfig.enum';
import { AppContext } from 'src/contexts/app.context';
import i18n from 'src/translation/i18n';
import type { Language, Theme } from 'src/types/webConfig.type';

interface Props {
  webConfigData?: any;
}

const GeneralConfig = ({ webConfigData }: Props) => {
  const [form] = Form.useForm();
  const [theme, setTheme] = useState<Theme>('light');
  const [language, setLanguage] = useState<Language>('vi');

  const {
    theme: themeFromContext,
    setTheme: setThemeFromContext,
    language: languageFromContext,
    setLanguage: setLanguageFromContext
  } = useContext(AppContext);

  const { t } = useTranslation();

  // Set form values from API data
  useEffect(() => {
    if (webConfigData?.data) {
      const configs = webConfigData.data;
      const themeConfig = configs.find((config: any) => config.key === WebConfigEn.theme);
      const languageConfig = configs.find((config: any) => config.key === WebConfigEn.language);

      if (themeConfig) {
        setTheme(themeConfig.value as Theme);
        form.setFieldValue('theme', themeConfig.value);
        localStorage.setItem(WebConfigEn.theme, themeConfig.value);
      }

      if (languageConfig) {
        setLanguage(languageConfig.value as Language);
        form.setFieldValue('language', languageConfig.value);
        localStorage.setItem(WebConfigEn.language, languageConfig.value);
      }
    }
  }, [webConfigData, form]);

  useEffect(() => {
    if (themeFromContext || languageFromContext) {
      setTheme(themeFromContext as Theme);
      localStorage.setItem(WebConfigEn.theme, String(themeFromContext));
      setLanguage(languageFromContext as Language);
      localStorage.setItem(WebConfigEn.language, String(languageFromContext));
    }
  }, [themeFromContext, languageFromContext]);

  // Define fields array like the sample code
  const fields = [
    {
      name: 'theme',
      value: theme,
      isDisabled: false
    },
    {
      name: 'language',
      value: language,
      isDisabled: false
    }
  ];

  return (
    <div className='setting-tab-content'>
      <Form form={form} layout='vertical' fields={fields}>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item label={t('value.theme')} name='theme'>
              <Select
                value={theme}
                onChange={(value) => {
                  setTheme(value);
                  setThemeFromContext(value);
                  form.setFieldValue('theme', value);
                  localStorage.setItem(WebConfigEn.theme, value);
                }}
                size='large'
                options={[
                  {
                    label: t('value.light'),
                    value: WebConfigEn.light
                  },
                  {
                    label: t('value.dark'),
                    value: WebConfigEn.dark
                  }
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label={t('value.language')} name='language'>
              <Select
                value={language}
                onChange={(value) => {
                  setLanguage(value);
                  setLanguageFromContext(value);
                  i18n.language !== value && i18n.changeLanguage(value);
                  form.setFieldValue('language', value);
                  localStorage.setItem(WebConfigEn.language, value);
                }}
                size='large'
                options={[
                  {
                    label: t('value.vi'),
                    value: WebConfigEn.vietnamese
                  },
                  {
                    label: t('value.en'),
                    value: WebConfigEn.english
                  }
                ]}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default GeneralConfig;
